import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { appAnalyticsAPI } from '../../services/api';
import { AppNav, AppStyles, fmtAgo, fmtDate, fmtMoney, fmtNum, platformIcon } from './common';

const AppUsersPage: React.FC = () => {
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') || '');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const page = Number(params.get('page') || 1);
  const reached = params.get('reached') || '';
  const identified = params.get('identified') || '';

  useEffect(() => {
    if (!assetId) return;
    setLoading(true);
    appAnalyticsAPI.getUsers({ asset_id: assetId, page, limit: 25, search: params.get('search') || undefined, reached: reached || undefined, identified: identified || undefined })
      .then(r => setData(r.data?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [assetId, params, page, reached, identified]);

  const update = (patch: Record<string, string>) => {
    const p = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    if (!('page' in patch)) p.delete('page');
    setParams(p);
  };

  const lsteps: any[] = data?.lifecycle_steps || [];

  return (
    <DashboardLayout>
      <AppStyles />
      <div className="container-fluid py-4">
        <h4 className="fw-bold mb-0"><i className="bi bi-people text-primary me-2"></i>Users & journeys</h4>
        <p className="text-muted small mb-2">Every install and signed-in user, where they are in your lifecycle, and their full journey.</p>
        <AppNav />
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body py-2 d-flex flex-wrap gap-2 align-items-center">
            <form className="input-group input-group-sm" style={{ maxWidth: 320 }} onSubmit={e => { e.preventDefault(); update({ search }); }}>
              <input className="form-control" placeholder="Search user id, anonymous id, traits…" value={search} onChange={e => setSearch(e.target.value)} />
              <button className="btn btn-outline-secondary"><i className="bi bi-search"></i></button>
            </form>
            <select className="form-select form-select-sm" style={{ width: 230 }} value={reached} onChange={e => update({ reached: e.target.value })} aria-label="Reached step">
              <option value="">Any lifecycle stage</option>
              {lsteps.map(s => <option key={s.step_key} value={s.step_key}>Reached: {s.display_name}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width: 170 }} value={identified} onChange={e => update({ identified: e.target.value })} aria-label="Identified">
              <option value="">All users</option>
              <option value="yes">Signed-in (identified)</option>
              <option value="no">Anonymous only</option>
            </select>
            <span className="ms-auto small text-muted">{fmtNum(data?.total || 0)} users</span>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0 small">
              <thead className="table-light">
                <tr><th>User</th><th>Lifecycle stage</th><th>Device</th><th>Sessions</th><th>Purchases</th><th>First seen</th><th>Last seen</th></tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>}
                {!loading && (data?.users || []).length === 0 && (
                  <tr><td colSpan={7} className="text-center text-muted py-4">No users yet. <Link to="/app-analytics/setup">Integrate the SDK →</Link></td></tr>
                )}
                {!loading && (data?.users || []).map((u: any) => (
                  <tr key={u.id}>
                    <td>
                      <Link to={`/app-analytics/users/${u.id}`} className="fw-semibold text-decoration-none">
                        {u.identified ? u.user_id : <span className="text-muted">Anonymous · {String(u.anonymous_id).slice(0, 8)}</span>}
                      </Link>
                      {u.identified ? <span className="badge bg-primary-subtle text-primary ms-1">identified</span> : null}
                      {u.devices > 1 && <span className="badge bg-light text-dark border ms-1">{u.devices} devices</span>}
                      {u.country && <div className="text-muted" style={{ fontSize: 11 }}><i className="bi bi-geo-alt me-1"></i>{u.country}</div>}
                    </td>
                    <td style={{ minWidth: 180 }}>
                      {u.stage ? (
                        <>
                          <span className="badge bg-primary-subtle text-primary ca-stage-pill">{u.stage.display_name}</span>
                          <div className="progress mt-1" style={{ height: 4, width: 140 }}>
                            <div className="progress-bar" style={{ width: `${((u.stage.index + 1) / u.stage.of) * 100}%`, background: '#8b5cf6' }}></div>
                          </div>
                        </>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td><i className={`bi ${platformIcon(u.platform)} me-1`}></i>{u.platform || '—'} {u.app_version ? <span className="text-muted">v{u.app_version}</span> : null}
                      <div className="text-muted" style={{ fontSize: 11 }}>{[u.device_model, u.os && `${u.os} ${u.os_version || ''}`].filter(Boolean).join(' · ')}</div></td>
                    <td>{fmtNum(u.sessions)}</td>
                    <td>{u.purchases ? <><strong>{fmtNum(u.purchases)}</strong> · {fmtMoney(u.revenue)}</> : <span className="text-muted">—</span>}</td>
                    <td title={fmtDate(u.first_seen_at)}>{fmtAgo(u.first_seen_at)}</td>
                    <td title={fmtDate(u.last_seen_at)}>{fmtAgo(u.last_seen_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data && data.total_pages > 1 && (
            <div className="card-footer bg-white d-flex justify-content-between align-items-center small">
              <span>Page {page} of {data.total_pages}</span>
              <div className="btn-group btn-group-sm">
                <button className="btn btn-outline-secondary" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Previous</button>
                <button className="btn btn-outline-secondary" disabled={page >= data.total_pages} onClick={() => update({ page: String(page + 1) })}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AppUsersPage;
